import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { usersApi } from '../../../api/users.js';
import { rolesApi } from '../../../api/roles.js';
import { storesApi } from '../../../api/stores.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

import { UserFormModal } from './UserFormModal.jsx';
import { PasswordResetModal } from './PasswordResetModal.jsx';

const STATUS_TONE = { active: 'success', disabled: 'neutral', locked: 'warning', pending: 'info' };
const STATUS_LABEL = { active: 'Attivo', disabled: 'Disattivato', locked: 'Bloccato', pending: 'In attesa' };

export function UsersPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canCreate = hasPermission('users:create');
  const canUpdate = hasPermission('users:update');
  const canDelete = hasPermission('users:delete');

  const rolesQuery = useQuery({ queryKey: ['roles', 'filter'], queryFn: () => rolesApi.list({ limit: 100 }) });
  const storesQuery = useQuery({ queryKey: ['stores', 'filter'], queryFn: () => storesApi.list({ limit: 100 }) });

  const roleMap = useMemo(() => {
    const items = (rolesQuery.data && rolesQuery.data.items) || [];
    return new Map(items.map((r) => [String(r._id), r]));
  }, [rolesQuery.data]);
  const storeMap = useMemo(() => {
    const items = (storesQuery.data && storesQuery.data.items) || [];
    return new Map(items.map((s) => [String(s._id), s]));
  }, [storesQuery.data]);

  const { items, isLoading, pagination, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['users', 'list'],
    fetcher: usersApi.list
  });

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: (id) => run(() => usersApi.remove(id)),
    onSuccess: () => {
      toast.success('Utente eliminato.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setConfirmTarget(null);
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const unlockMutation = useMutation({
    mutationFn: (id) => usersApi.unlock(id),
    onSuccess: () => {
      toast.success('Account sbloccato.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const columns = [
    { key: 'email', header: 'Email', render: (r) => <span className="text-ink">{r.email}</span> },
    {
      key: 'name',
      header: 'Nome',
      render: (r) => [r.firstName, r.lastName].filter(Boolean).join(' ') || <span className="text-ink-faint">-</span>
    },
    {
      key: 'role',
      header: 'Ruolo',
      render: (r) => {
        const roleId = r.roleId && (r.roleId._id || r.roleId);
        const role = roleMap.get(String(roleId));
        return role ? <Badge tone={role.isSystem ? 'info' : 'neutral'}>{role.label}</Badge> : <span className="text-ink-faint">-</span>;
      }
    },
    {
      key: 'store',
      header: 'Negozi',
      render: (r) => {
        const raw = r.storeIds || (r.storeId ? [r.storeId] : []);
        const names = raw
          .map((s) => {
            const id = s && (s._id || s);
            const store = storeMap.get(String(id));
            return (store && store.name) || (s && s.name) || null;
          })
          .filter(Boolean);
        if (names.length === 0) return <span className="text-ink-faint">-</span>;
        return (
          <span className="flex flex-wrap gap-1">
            {names.map((name, i) => <Badge key={i} tone="neutral">{name}</Badge>)}
          </span>
        );
      }
    },
    {
      key: 'status',
      header: 'Stato',
      render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || r.status}</Badge>
    },
    {
      key: 'mfaEnabled',
      header: 'MFA',
      render: (r) => r.mfaEnabled ? <Badge tone="success">On</Badge> : <Badge>Off</Badge>
    },
    {
      key: 'lastLoginAt',
      header: 'Ultimo accesso',
      render: (r) => r.lastLoginAt ? <span className="text-xs text-ink-muted">{fmt.dateTime(r.lastLoginAt)}</span> : <span className="text-xs text-ink-faint">mai</span>
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex justify-end gap-2">
          {canUpdate && r.status === 'locked' && (
            <button type="button" className="text-xs font-medium text-warning hover:underline disabled:opacity-50" disabled={unlockMutation.isPending} onClick={() => unlockMutation.mutate(r._id)}>
              Sblocca
            </button>
          )}
          {canUpdate && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => { setEditing(r); setFormOpen(true); }}>
              Modifica
            </button>
          )}
          {canUpdate && (
            <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setPasswordTarget(r)}>
              Password
            </button>
          )}
          {canDelete && (
            <button type="button" className="text-xs text-danger hover:underline" onClick={() => setConfirmTarget(r)}>
              Elimina
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t('nav.users')}
        description="Operatori del sistema, divisi per ruolo e punto vendita."
        actions={canCreate ? <Button onClick={() => { setEditing(null); setFormOpen(true); }}>Nuovo utente</Button> : null}
      />

      <div className="flex flex-wrap items-end gap-3">
        <Input
          className="w-64"
          label="Cerca"
          placeholder="email..."
          value={filters.q || ''}
          onChange={(e) => setFilters({ q: e.target.value || undefined })}
        />
        <Select
          label="Stato"
          className="w-40"
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
        >
          <option value="">Tutti</option>
          <option value="active">Attivi</option>
          <option value="disabled">Disattivati</option>
          <option value="locked">Bloccati</option>
          <option value="pending">In attesa</option>
        </Select>
        <Select
          label="Ruolo"
          className="w-48"
          value={filters.roleId || ''}
          onChange={(e) => setFilters({ roleId: e.target.value || undefined })}
        >
          <option value="">Tutti</option>
          {Array.from(roleMap.values()).map((r) => (
            <option key={r._id} value={r._id}>{r.label}</option>
          ))}
        </Select>
      </div>

      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessun utente', description: 'Crea il primo operatore per popolare il sistema.' }}
      />

      <Pagination pagination={pagination} onPageChange={setPage} />

      <UserFormModal open={formOpen} onClose={() => { setFormOpen(false); setEditing(null); }} initial={editing} />
      <PasswordResetModal open={Boolean(passwordTarget)} onClose={() => setPasswordTarget(null)} user={passwordTarget} />
      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Eliminare l'utente?"
        description={confirmTarget ? `L'account ${confirmTarget.email} sarà rimosso. Serve una verifica MFA recente.` : ''}
        destructive
        confirmLabel="Elimina"
        loading={deleteMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => deleteMutation.mutate(confirmTarget._id)}
      />
      <MfaPrompt {...mfaProps} />
    </div>
  );
}
